import { useState, useCallback } from 'react';
import { SimpliPayConfig, PaymentData, TransactionResult, TransactionError, CheckoutCallbacks } from '../types';

interface UseSimplipayReturn {
  isOpen: boolean;
  openCheckout: (paymentData: PaymentData) => void;
  closeCheckout: () => void;
  paymentData: PaymentData | null;
  result: TransactionResult | null;
  error: TransactionError | null;
}

/**
 * Hook for programmatic control of SimpliPay Checkout
 * 
 * @example
 * ```tsx
 * const { isOpen, openCheckout, closeCheckout, result, error } = useSimplipay({
 *   publicKey: 'pk_test_xxxxx',
 *   environment: 'development'
 * });
 * 
 * // Open checkout
 * openCheckout({
 *   email: 'customer@example.com',
 *   phone: '+2348012345678',
 *   amount: 5000,
 *   currency: 'NGN',
 *   transactionRef: 'TXN-123',
 *   customerId: 'CUST-456'
 * });
 * ```
 */
export const useSimplipay = (config: SimpliPayConfig): UseSimplipayReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [result, setResult] = useState<TransactionResult | null>(null);
  const [error, setError] = useState<TransactionError | null>(null);

  const openCheckout = useCallback((data: PaymentData) => {
    setPaymentData(data);
    setResult(null);
    setError(null);
    setIsOpen(true);
  }, []);

  const closeCheckout = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    openCheckout,
    closeCheckout,
    paymentData,
    result,
    error,
  };
};

export default useSimplipay;
